import Cookies from "js-cookie";
import {
    ensureChatSocket,
    trackPendingUpload,
    patchPendingUpload,
    clearPendingUpload,
} from "./chatSocketManager";
import {
    trackGroupPendingUpload,
    patchGroupPendingUpload,
    clearGroupPendingUpload,
} from "./groupSocketManager";
import {
    savePersistedUpload,
    updatePersistedUpload,
    removePersistedUpload,
    listPersistedUploads,
    blobToFile,
} from "./uploadPersistence";
import { MEDIA_UPLOAD_TIMEOUT_MS } from "../utils/mediaUploadLogger";

const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const resolveServerFileUrl = (rawUrl) => {
    if (!rawUrl) return null;
    const value = String(rawUrl);
    if (value.startsWith("blob:") || value.startsWith("http")) return value;
    return `${process.env.REACT_APP_API_BASE_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

/** @type {Map<string, object>} */
const globalUploads = new Map();
/** @type {Set<(uploads: object[]) => void>} */
const listeners = new Set();
/** @type {Set<string>} */
const inFlight = new Set();
/** Uploads owned by the live chat/group UI path (not a post-reload resume). */
/** @type {Set<string>} */
const liveSessionOwned = new Set();
let resumeStarted = false;
let connectHookAttached = false;

const notify = () => {
    const snapshot = getGlobalUploads();
    listeners.forEach((listener) => {
        try {
            listener(snapshot);
        } catch {
            /* ignore */
        }
    });
};

const setGlobalUpload = (id, patch) => {
    const key = String(id);
    const prev = globalUploads.get(key) || { id: key };
    const next = { ...prev, ...patch, id: key, updatedAt: Date.now() };
    globalUploads.set(key, next);
    notify();
    return next;
};

const removeGlobalUpload = (id) => {
    const key = String(id);
    const existing = globalUploads.get(key);
    if (existing?.previewUrl?.startsWith?.("blob:")) {
        try {
            URL.revokeObjectURL(existing.previewUrl);
        } catch {
            /* ignore */
        }
    }
    globalUploads.delete(key);
    notify();
};

export const getGlobalUploads = () =>
    [...globalUploads.values()].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

export const subscribeGlobalUploads = (listener) => {
    listeners.add(listener);
    try {
        listener(getGlobalUploads());
    } catch {
        /* ignore */
    }
    return () => listeners.delete(listener);
};

const waitForSocket = (timeoutMs = 20000) =>
    new Promise((resolve, reject) => {
        const token = Cookies.get("auth_token");
        const socket = ensureChatSocket(token);
        if (!socket) {
            reject(new Error("Not connected to server"));
            return;
        }
        if (socket.connected) {
            resolve(socket);
            return;
        }
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error("Socket connection timed out"));
        }, timeoutMs);
        const onConnect = () => {
            cleanup();
            resolve(socket);
        };
        const onError = (err) => {
            cleanup();
            reject(err || new Error("Socket connection failed"));
        };
        const cleanup = () => {
            clearTimeout(timer);
            socket.off("connect", onConnect);
            socket.off("connect_error", onError);
        };
        socket.on("connect", onConnect);
        socket.on("connect_error", onError);
        if (typeof socket.connect === "function" && socket.disconnected) {
            socket.connect();
        }
    });

const buildOptimisticMessage = ({ tempId, file, fileType, shareType, previewUrl }) => ({
    id: tempId,
    text: file.name,
    type: fileType,
    isUser: true,
    time: new Date().toISOString(),
    fileName: file.name,
    fileUrl: previewUrl,
    mime: file.type || undefined,
    fileSize: formatFileSize(file.size),
    _rawFileSize: file.size,
    _normalizedFileName: String(file.name || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ""),
    uploading: true,
    tempId,
    _clientUploadId: tempId,
    uploadProgress: 0,
    uploadStartedAt: Date.now(),
    shareType: shareType || "",
});

const readFileAsBytes = (file, onProgress) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onprogress = (e) => {
            if (!e.lengthComputable) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            onProgress?.(pct);
        };
        reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
        reader.onload = () => resolve(new Uint8Array(reader.result));
        reader.readAsArrayBuffer(file);
    });

const finishSuccess = async (tempId, kind, targetId, patch) => {
    if (kind === "group") {
        patchGroupPendingUpload(targetId, tempId, patch, { notify: true });
        clearGroupPendingUpload(targetId, tempId, { notify: false });
    } else {
        patchPendingUpload(targetId, tempId, patch, { notify: true });
        clearPendingUpload(targetId, tempId, { notify: false });
    }
    await removePersistedUpload(tempId);
    removeGlobalUpload(tempId);
    inFlight.delete(String(tempId));
};

const finishFailure = async (tempId, kind, targetId, errorMsg) => {
    const patch = {
        uploading: false,
        uploadFailed: true,
        uploadError: errorMsg || "Upload failed",
        uploadProgress: 0,
    };
    if (kind === "group") {
        patchGroupPendingUpload(targetId, tempId, patch, { notify: true });
    } else {
        patchPendingUpload(targetId, tempId, patch, { notify: true });
    }
    setGlobalUpload(tempId, {
        status: "failed",
        progress: 0,
        error: errorMsg || "Upload failed",
    });
    await removePersistedUpload(tempId);
    window.setTimeout(() => removeGlobalUpload(tempId), 4000);
    inFlight.delete(String(tempId));
};

const runUploadPipeline = async ({
    tempId,
    kind,
    targetId,
    file,
    fileType,
    shareType,
    previewUrl,
    restored = false,
}) => {
    const key = String(tempId);
    if (inFlight.has(key)) return;
    inFlight.add(key);

    const optimisticMessage = buildOptimisticMessage({
        tempId,
        file,
        fileType,
        shareType,
        previewUrl,
    });

    setGlobalUpload(tempId, {
        id: tempId,
        kind,
        targetId: String(targetId),
        fileName: file.name,
        fileType,
        mime: file.type || "",
        fileSize: file.size,
        progress: restored ? 5 : 0,
        status: restored ? "resuming" : "reading",
        previewUrl: previewUrl || null,
        createdAt: optimisticMessage.uploadStartedAt,
        error: null,
    });

    try {
        await savePersistedUpload({
            id: tempId,
            kind,
            targetId,
            fileName: file.name,
            mime: file.type || "",
            shareType: shareType || "",
            fileType,
            fileSize: file.size,
            progress: 0,
            status: "pending",
            createdAt: optimisticMessage.uploadStartedAt,
            blob: file,
        });
    } catch {
        // Persistence is best-effort; upload can still proceed in this session.
    }

    if (kind === "group") {
        trackGroupPendingUpload(targetId, tempId, optimisticMessage, undefined, { notify: true });
    } else {
        trackPendingUpload(targetId, tempId, optimisticMessage, undefined, { notify: true });
    }

    const timeoutId = window.setTimeout(() => {
        if (!inFlight.has(key)) return;
        finishFailure(
            tempId,
            kind,
            targetId,
            "Upload timed out — the server did not respond. Please try again."
        );
    }, MEDIA_UPLOAD_TIMEOUT_MS);

    try {
        const socket = await waitForSocket();
        const bytes = await readFileAsBytes(file, (pct) => {
            setGlobalUpload(tempId, { progress: pct, status: "reading" });
            updatePersistedUpload(tempId, { progress: pct, status: "reading" });
            if (kind === "group") {
                patchGroupPendingUpload(targetId, tempId, { uploadProgress: pct }, { notify: true });
            } else {
                patchPendingUpload(targetId, tempId, { uploadProgress: pct }, { notify: true });
            }
        });

        setGlobalUpload(tempId, { progress: 100, status: "uploading" });
        updatePersistedUpload(tempId, { progress: 100, status: "uploading" });
        if (kind === "group") {
            patchGroupPendingUpload(targetId, tempId, { uploadProgress: 100 }, { notify: true });
        } else {
            patchPendingUpload(targetId, tempId, { uploadProgress: 100 }, { notify: true });
        }

        await new Promise((resolve, reject) => {
            if (kind === "group") {
                const token = Cookies.get("auth_token");
                if (!token) {
                    reject(new Error("Not authenticated"));
                    return;
                }
                socket.emit(
                    "send_group_file",
                    {
                        token,
                        group_id: targetId,
                        filename: file.name,
                        mime_type: file.type || undefined,
                        file: bytes,
                        share_type: shareType || "",
                    },
                    (ack) => {
                        if (ack?.error) {
                            reject(new Error(ack.error));
                            return;
                        }
                        const returnedFile =
                            ack &&
                            (ack.file_url || ack.fileUrl || ack.url || ack.path || ack.file_path);
                        const serverMessageId =
                            ack?.message_id || ack?.id || ack?.data?.message_id || null;
                        const resolved =
                            typeof returnedFile === "string"
                                ? returnedFile
                                : returnedFile?.url ||
                                  returnedFile?.path ||
                                  returnedFile?.file_url ||
                                  "";
                        const patch = {
                            uploading: false,
                            uploadFailed: false,
                            uploadError: undefined,
                            fileUrl: resolved ? resolveServerFileUrl(resolved) : undefined,
                            fileName: ack?.filename || ack?.fileName || file.name,
                            mime: ack?.mime_type || ack?.mime || file.type || undefined,
                            thumbnail_image: ack?.thumbnail_image || ack?.thumbnail_path || null,
                            tempId: undefined,
                            _clientUploadId: tempId,
                            type: fileType,
                            ...(serverMessageId ? { id: serverMessageId } : {}),
                        };
                        finishSuccess(tempId, kind, targetId, patch).then(resolve).catch(resolve);
                    }
                );
                return;
            }

            socket.emit(
                "send_file",
                {
                    to_user_id: targetId,
                    filename: file.name,
                    mime_type: file.type || undefined,
                    caption: "",
                    file: bytes,
                    share_type: shareType || "",
                    client_time: new Date().toISOString(),
                },
                (ack) => {
                    if (ack?.error) {
                        reject(new Error(ack.error));
                        return;
                    }
                    const returnedFile =
                        ack && (ack.file_url || ack.fileUrl || ack.url || ack.path || ack.file_path);
                    const serverMessageId =
                        ack?.message_id || ack?.id || ack?.data?.message_id || null;
                    const resolved =
                        typeof returnedFile === "string"
                            ? returnedFile
                            : returnedFile?.url || returnedFile?.path || returnedFile?.file_path || "";
                    const patch = {
                        uploading: false,
                        uploadFailed: false,
                        uploadError: undefined,
                        fileUrl: resolved ? resolveServerFileUrl(resolved) : undefined,
                        fileUrlRaw: resolved || undefined,
                        fileName: ack?.filename || ack?.fileName || file.name,
                        mime: ack?.mime_type || ack?.mime || file.type || undefined,
                        thumbnail_image: ack?.thumbnail_image || ack?.thumbnail_path || null,
                        tempId: undefined,
                        _clientUploadId: undefined,
                        type: fileType,
                        ...(serverMessageId ? { id: serverMessageId } : {}),
                    };
                    finishSuccess(tempId, kind, targetId, patch).then(resolve).catch(resolve);
                }
            );
        });
    } catch (err) {
        await finishFailure(tempId, kind, targetId, err?.message || "Upload failed");
        throw err;
    } finally {
        window.clearTimeout(timeoutId);
        inFlight.delete(key);
    }
};

/**
 * Lightweight hooks for existing chatScreen/group upload paths that still own the emit.
 * Keeps the global loader + IndexedDB in sync so uploads can resume after refresh.
 */
export const mirrorUploadStarted = async ({
    tempId,
    kind,
    targetId,
    file,
    fileType,
    shareType = "",
    previewUrl = null,
}) => {
    if (!tempId || !file || !targetId) return;
    liveSessionOwned.add(String(tempId));
    setGlobalUpload(tempId, {
        id: tempId,
        kind: kind === "group" ? "group" : "dm",
        targetId: String(targetId),
        fileName: file.name,
        fileType,
        mime: file.type || "",
        fileSize: file.size,
        progress: 0,
        status: "reading",
        previewUrl,
        createdAt: Date.now(),
        error: null,
    });
    try {
        await savePersistedUpload({
            id: tempId,
            kind: kind === "group" ? "group" : "dm",
            targetId,
            fileName: file.name,
            mime: file.type || "",
            shareType,
            fileType,
            fileSize: file.size,
            progress: 0,
            status: "pending",
            createdAt: Date.now(),
            blob: file,
        });
    } catch {
        /* ignore */
    }
};

export const mirrorUploadProgress = (tempId, progress, status = "reading") => {
    if (!tempId) return;
    setGlobalUpload(tempId, { progress, status });
    updatePersistedUpload(tempId, { progress, status });
};

export const mirrorUploadFinished = async (tempId, { failed = false, error = null } = {}) => {
    if (!tempId) return;
    liveSessionOwned.delete(String(tempId));
    await removePersistedUpload(tempId);
    if (failed) {
        setGlobalUpload(tempId, {
            status: "failed",
            progress: 0,
            error: error || "Upload failed",
        });
        window.setTimeout(() => removeGlobalUpload(tempId), 4000);
        return;
    }
    removeGlobalUpload(tempId);
};

export const resumePersistedUploads = async () => {
    if (resumeStarted) return;
    resumeStarted = true;

    try {
        const rows = await listPersistedUploads();
        if (!rows.length) return;

        const token = Cookies.get("auth_token");
        if (!token) return;
        ensureChatSocket(token);

        for (const row of rows) {
            const id = String(row.id);
            // Skip uploads still running in this same page session
            if (inFlight.has(id) || liveSessionOwned.has(id)) continue;
            if (!row.blob || !row.targetId) {
                await removePersistedUpload(id);
                continue;
            }

            const file = blobToFile(row.blob, row.fileName, row.mime);
            const previewUrl =
                row.fileType === "image" || row.fileType === "video"
                    ? URL.createObjectURL(file)
                    : null;

            setGlobalUpload(id, {
                id,
                kind: row.kind === "group" ? "group" : "dm",
                targetId: String(row.targetId),
                fileName: row.fileName,
                fileType: row.fileType,
                mime: row.mime,
                fileSize: row.fileSize || file.size,
                progress: Math.max(5, Number(row.progress) || 0),
                status: "resuming",
                previewUrl,
                createdAt: row.createdAt || Date.now(),
                error: null,
            });

            runUploadPipeline({
                tempId: id,
                kind: row.kind === "group" ? "group" : "dm",
                targetId: row.targetId,
                file,
                fileType: row.fileType || "document",
                shareType: row.shareType || "",
                previewUrl,
                restored: true,
            }).catch(() => {
                /* handled */
            });
        }
    } finally {
        window.setTimeout(() => {
            resumeStarted = false;
        }, 2000);
    }
};

export const attachDurableUploadResumeHook = () => {
    if (connectHookAttached) return;
    const token = Cookies.get("auth_token");
    const socket = ensureChatSocket(token);
    if (!socket) return;

    connectHookAttached = true;
    const onConnect = () => {
        resumePersistedUploads();
    };
    socket.on("connect", onConnect);
    if (socket.connected) {
        resumePersistedUploads();
    }
};
